const mapAttribute = (attribute) => {
  const mappedAttribute = {
    questionId: attribute.questionId
  };
  if (attribute.responses) {
    mappedAttribute.responseIds = attribute.responses.map(response => response.answerId);
  } else if (attribute.responseIds) {
    mappedAttribute.responseIds = attribute.responseIds;
  } else if (attribute.textualResponses == null) {
    return mappedAttribute;
  } else {
    mappedAttribute.textualResponses = attribute.textualResponses.map(response => ({
      locale: response.locale,
      response: response.textResponse
    }));
  }
  return mappedAttribute;
};

const requestModelForGraphQL = (product, exhibitingOrganisationId, locale, ppsQuestions) => {
  const filterTaggedPPSQuestionIds = ppsQuestions.filter(ppsQuestion => ppsQuestion.tags.includes('Filters')).map(ppsQues => ppsQues.id);
  const response = {
    videoId: product.videoId,
    imageUrl: product.imageUrl || '',
    isInnovative: product.isInnovative,
    isNew: product.isNew,
    isPublished: product.isPublished,
    multilingual: [{
      title: product.name || '',
      description: product.description || '',
      locale
    }],
    extraCharacteristics: product.extraCharacteristics,
    attributes:
      product.attributes.filter(attribute => !filterTaggedPPSQuestionIds.includes(attribute.questionId)
       && (attribute.responses != null || attribute.textualResponses != null))
        .map(attribute => (mapAttribute(attribute))),
    filterAttributes:
    product.attributes.filter(attribute => filterTaggedPPSQuestionIds.includes(attribute.questionId) && attribute.responses != null)
      .map(attribute => (mapAttribute(attribute)))
  };
  if (product.id == null) {
    return {
      exhibitingOrganisationId,
      product: response
    };
  }

  return {
    productId: product.id,
    product: response
  };
};

const requestModel = (product, exhibitingOrganisationId, locale) => ({
  imageUrl: product.imageUrl,
  exhibitingOrganisationId,
  isInnovative: product.isInnovative,
  isNew: product.isNew,
  multilingual: [{
    name: product.name,
    description: product.description,
    locale
  }]
});

const findDataForLocale = (multilingualText, interfaceLocale, defaultLocale) => {
  const availableLocales = multilingualText.map(attr => attr.locale.toLowerCase());
  if (availableLocales.includes(interfaceLocale.toLowerCase())) {
    return multilingualText.find(attr => attr.locale.toLowerCase() === interfaceLocale.toLowerCase());
  }
  return multilingualText.find(attr => attr.locale.toLowerCase() === defaultLocale.toLowerCase());
};

const getAnswer = (attribute, interfaceLocale, primaryLocale) => {
  if (attribute.textualResponses !== null) {
    const textualResponse = attribute.textualResponses.find(x => x.locale.toLowerCase() === interfaceLocale.toLowerCase());
    return textualResponse ? [textualResponse.textResponse] : [null];
  } else if (attribute.responses !== null) {
    return attribute.responses.map(res => findDataForLocale(res.answerText, interfaceLocale, primaryLocale).name);
  } else {
    return [];
  }
}

const fromResponseWithAttributes = (productsResponse, interfaceLocale, primaryLocale) => {
  const { products } = productsResponse;

  const result = products.map((p) => {
    let lingualAttributes = p.attributes ? p.attributes.map(attr => Object.assign(
      {}, Array.from(attr).map(x => x),
      {
        question: findDataForLocale(attr.questionText, interfaceLocale, primaryLocale).name,
        answers: getAnswer(attr, interfaceLocale, primaryLocale),
        sortOrder: attr.sortOrder
      }
    )) : null;

    if (lingualAttributes) {
      lingualAttributes = lingualAttributes.filter(at => at.answers[0]);
    }
    const lingualFilterAttributes = p.filterAttributes !== null ? p.filterAttributes.map(fa => Object.assign(
      {}, Array.from(fa).map(x => x),
      {
        question: findDataForLocale(fa.questionText, interfaceLocale, primaryLocale).name,
        answers: fa.responses.map(res => findDataForLocale(res.answerText, interfaceLocale, primaryLocale).name),
        sortOrder: fa.sortOrder
      }
    )) : null;

    const lingual = findDataForLocale(p.multilingual, interfaceLocale, primaryLocale);
    return {
      id: p.id,
      name: lingual ? lingual.name || lingual.title : '',
      description: lingual ? lingual.description : '',
      imageUrl: p.imageUrl,
      isInnovative: p.isInnovative,
      isNew: p.isNew,
      exhibitingOrganisationId: p.exhibitingOrganisationId,
      attributes: lingualAttributes,
      filterAttributes: lingualFilterAttributes,
      producerDetails: p.producerDetails,
      videoId: p.videoId,
      video: p.video,
      partnerVideoUuid: p.partnerVideoUuid,
      documents: p.documents
    };
  });
  return { products: result };
};

const mapAnswer = (element, locale) => {
  const answer = { };
  answer.questionId = element.id;
  if (element.responses !== null && element.responses !== undefined) {
    answer.responseIds = [];
    element.responses.map(x => answer.responseIds.push(x.answerId));
  }
  if (element.textualResponses !== null && element.textualResponses !== undefined) {
    const val = element.textualResponses.find(x => x.locale === locale);
    if (val !== null && val !== undefined) {
      answer.textualResponse = val.textResponse;
    }
  }
  return answer;
};

const fromResponse = (productsResponse, interfaceLocale, primaryLocale) => {
  const { products } = productsResponse;
  const result = products.map((p) => {
    let lingual = p.multilingual.find(x => x.locale.toLowerCase() === interfaceLocale.toLowerCase());
    if (primaryLocale !== undefined && !lingual) {
      lingual = p.multilingual.find(x => x.locale.toLowerCase() === primaryLocale.toLowerCase());
    }
    const attributes = [];
    if (p.filterAttributes !== null && p.filterAttributes !== undefined) {
      p.filterAttributes.forEach((element) => {
        attributes.push(mapAnswer(element, interfaceLocale));
      });
    }
    if (p.attributes !== null && p.attributes !== undefined) {
      p.attributes.forEach((element) => {
        attributes.push(mapAnswer(element, interfaceLocale));
      });
    }

    return {
      id: p.id,
      name: lingual ? lingual.name || lingual.title : '',
      description: lingual ? lingual.description : '',
      imageUrl: p.imageUrl,
      isInnovative: p.isInnovative,
      isNew: p.isNew,
      exhibitingOrganisationId: p.exhibitingOrganisationId,
      attributes,
      producerDetails: p.producerDetails,
      videoId: p.videoId,
      video: p.video,
      documents: p.documents,
      extraCharacteristics: p.extraCharacteristics ? p.extraCharacteristics.map(x=>x.id) : [],
      isPublished: p.isPublished,
      lastUpdatedAt: p.lastUpdatedAt
    };
  });
  return { products: result };
};

const toLowerIfNotNull = data => data && data.toLowerCase();
const getLocaleSpecificData = (multilingual, locale) =>
  locale && multilingual.find(x => x.locale.toLowerCase() === locale);

const fromProfileResponse = (productsResponse, requestLocale, primaryLocale) => {
  const { products } = productsResponse;
  const result = products.map((p) => {
    const requestLocaleInLower = toLowerIfNotNull(requestLocale);
    const primaryLocaleInLower = toLowerIfNotNull(primaryLocale);

    const lingual = getLocaleSpecificData(p.multilingual, requestLocaleInLower);
    const primaryLingual = primaryLocaleInLower === requestLocaleInLower ?
      null : getLocaleSpecificData(p.multilingual, primaryLocaleInLower);
    return {
      id: p.id,
      name: lingual ? lingual.name : '',
      description: lingual ? lingual.description : '',
      imageUrl: p.imageUrl,
      isInnovative: p.isInnovative,
      isNew: p.isNew,
      primaryLocaleName: !primaryLingual ? null : primaryLingual.name,
      primaryLocaleDescription: !primaryLingual ? null : primaryLingual.description,
      documents: p.documents,
      video: p.video
    };
  });
  return { products: result };
};

module.exports = {
  requestModel, requestModelForGraphQL, fromResponse, fromProfileResponse, fromResponseWithAttributes
};
